import React from 'react';
import styled from 'styled-components';
import { 
  FileText, 
  Download, 
  FileSpreadsheet,
  FileCheck,
  FileQuestion,
  ExternalLink,
  ChevronRight,
  Search,
  Filter
} from 'lucide-react';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
`;

const SearchBar = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
  min-width: 250px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.75rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }
  }
`;

const FilterSelect = styled.select`
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  background: var(--white);
  min-width: 200px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  overflow: hidden;
`;

const CategoryHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  padding: 1.5rem;
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 40px;
    height: 40px;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
    padding: 0.5rem;
  }

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
  }
`;

const FormsList = styled.div`
  padding: 1rem;
`;

const FormItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: var(--radius-md);
  transition: var(--transition);
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--background-light);
  }

  .icon {
    width: 44px;
    height: 44px;
    background: ${props => props.$type === 'pdf' ? 'rgba(220, 38, 38, 0.1)' : 'rgba(0, 150, 57, 0.1)'};
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.$type === 'pdf' ? '#dc2626' : '#009639'};
    font-size: 0.7rem;
    font-weight: 800;
  }

  .content {
    flex: 1;

    .title {
      font-weight: 600;
      color: var(--text-dark);
      margin-bottom: 0.25rem;
    }

    .meta {
      font-size: 0.85rem;
      color: var(--text-light);
    }
  }
`;

const DownloadBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: var(--primary-blue);
  color: var(--white);
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: var(--accent-gold);
    color: var(--primary-blue-dark);
  }
`;

const OnlineFormCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 38, 100, 0.05), rgba(254, 202, 0, 0.05));
  border-radius: var(--radius-xl);
  padding: 2rem;
  border: 2px solid var(--border-light);
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }

  .content {
    h3 {
      font-family: var(--font-secondary);
      font-size: 1.5rem;
      color: var(--text-dark);
      margin-bottom: 0.5rem;
    }

    p {
      color: var(--text-medium);
    }
  }
`;

const OnlineFormButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  padding: 1rem 2rem;
  border-radius: var(--radius-md);
  font-weight: 700;
  transition: var(--transition);
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
  }
`;

const formsData = {
  plaintes: [
    { id: 1, title: 'Formulaire de Réclamation standard', format: 'PDF', size: '125 Ko', type: 'pdf' },
    { id: 2, title: 'Formulaire de Réclamation anonyme', format: 'PDF', size: '98 Ko', type: 'pdf' },
    { id: 3, title: 'Modèle de lettre de Réclamation', format: 'DOCX', size: '45 Ko', type: 'doc' },
  ],
  administratifs: [
    { id: 4, title: 'Demande d\'information', format: 'PDF', size: '85 Ko', type: 'pdf' },
    { id: 5, title: 'Demande d\'audience', format: 'PDF', size: '72 Ko', type: 'pdf' },
    { id: 6, title: 'Autorisation de représentation', format: 'PDF', size: '68 Ko', type: 'pdf' },
  ],
  statistiques: [
    { id: 7, title: 'Questionnaire de satisfaction', format: 'PDF', size: '156 Ko', type: 'pdf' },
    { id: 8, title: 'Formulaire de suivi de médiation', format: 'XLSX', size: '89 Ko', type: 'doc' },
  ],
  guides: [
    { id: 9, title: 'Guide du plaignant', format: 'PDF', size: '2.4 Mo', type: 'pdf' },
    { id: 10, title: 'Brochure institutionnelle', format: 'PDF', size: '3.1 Mo', type: 'pdf' },
    { id: 11, title: 'Organigramme des services', format: 'PDF', size: '890 Ko', type: 'pdf' },
  ]
};

const Formulaires = () => {
  return (
    (<PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Formulaires</HeroTitle>
          <HeroSubtitle>
            Téléchargez tous les formulaires nécessaires pour vos démarches auprès de la Médiature
          </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <ContentWrapper>
          <SearchBar>
            <SearchInput>
              <Search size={20} />
              <input type="text" placeholder="Rechercher un formulaire..." />
            </SearchInput>
            <FilterSelect>
              <option value="all">Toutes les catégories</option>
              <option value="plaintes">Réclamations</option>
              <option value="administratifs">Documents administratifs</option>
              <option value="statistiques">Statistiques</option>
              <option value="guides">Guides et brochures</option>
            </FilterSelect>
          </SearchBar>

          <CategoriesGrid>
            <CategoryCard>
              <CategoryHeader>
                <FileText size={24} />
                <h3>Réclamations et réclamations</h3>
              </CategoryHeader>
              <FormsList>
                {formsData.plaintes.map(form => (
                  <FormItem key={form.id} $type={form.type === 'PDF' ? 'pdf' : 'doc'}>
                    <div className="icon">{form.format}</div>
                    <div className="content">
                      <div className="title">{form.title}</div>
                      <div className="meta">{form.format} ⬢ {form.size}</div>
                    </div>
                    <DownloadBtn href="#">
                      <Download size={16} />
                      Télécharger
                    </DownloadBtn>
                  </FormItem>
                ))}
              </FormsList>
            </CategoryCard>

            <CategoryCard>
              <CategoryHeader>
                <FileCheck size={24} />
                <h3>Documents administratifs</h3>
              </CategoryHeader>
              <FormsList>
                {formsData.administratifs.map(form => (
                  <FormItem key={form.id} $type={form.type === 'PDF' ? 'pdf' : 'doc'}>
                    <div className="icon">{form.format}</div>
                    <div className="content">
                      <div className="title">{form.title}</div>
                      <div className="meta">{form.format} ⬢ {form.size}</div>
                    </div>
                    <DownloadBtn href="#">
                      <Download size={16} />
                      Télécharger
                    </DownloadBtn>
                  </FormItem>
                ))}
              </FormsList>
            </CategoryCard>

            <CategoryCard>
              <CategoryHeader>
                <FileSpreadsheet size={24} />
                <h3>Statistiques et enquêtes</h3>
              </CategoryHeader>
              <FormsList>
                {formsData.statistiques.map(form => (
                  <FormItem key={form.id} $type={form.type === 'PDF' ? 'pdf' : 'doc'}>
                    <div className="icon">{form.format}</div>
                    <div className="content">
                      <div className="title">{form.title}</div>
                      <div className="meta">{form.format} ⬢ {form.size}</div>
                    </div>
                    <DownloadBtn href="#">
                      <Download size={16} />
                      Télécharger
                    </DownloadBtn>
                  </FormItem>
                ))}
              </FormsList>
            </CategoryCard>

            <CategoryCard>
              <CategoryHeader>
                <FileQuestion size={24} />
                <h3>Guides et brochures</h3>
              </CategoryHeader>
              <FormsList>
                {formsData.guides.map(form => (
                  <FormItem key={form.id} $type={form.type === 'PDF' ? 'pdf' : 'doc'}>
                    <div className="icon">{form.format}</div>
                    <div className="content">
                      <div className="title">{form.title}</div>
                      <div className="meta">{form.format} ⬢ {form.size}</div>
                    </div>
                    <DownloadBtn href="#">
                      <Download size={16} />
                      Télécharger
                    </DownloadBtn>
                  </FormItem>
                ))}
              </FormsList>
            </CategoryCard>
          </CategoriesGrid>

          <OnlineFormCard>
            <div className="content">
              <h3>Formulaire en ligne</h3>
              <p>Déposez votre Réclamation directement en ligne sans téléchargement</p>
            </div>
            <OnlineFormButton href="/services/depot-plainte">
              Accéder au formulaire
              <ExternalLink size={18} />
            </OnlineFormButton>
          </OnlineFormCard>
        </ContentWrapper>
      </Container>
    </PageContainer>)
  );
};

export default Formulaires;

